import React from "react";
import {Localizer} from "@/main/react/Types";

/**
 * The owner statement — a member's own short "what I come here for" line, rendered near the top of
 * their profile (slice P14 / agent F3, 2026-08-11).
 *
 * <h3>It is the SERVER-rendered block's twin, and it may only add to it</h3>
 *
 * `user.jte` already emits this block, with the same classes in the same position, because the
 * statement is self-authored public text on an indexed page and a crawler must read it without running
 * JS (rule 32). This component mounts over that markup via `UserHeader`'s `statementSlot`. Keep the
 * class strings and the ordering in step with the template; nothing links them at compile time, and a
 * position disagreement renders as a visible pop at the exact frame the island appears.
 *
 * The one deliberate asymmetry: the server renders the block only when there IS a statement, and this
 * component additionally renders the owner-facing invitation when there is not. So the island shows
 * strictly MORE than the server did, never less — the safe direction, and the only one rule 32 allows.
 * (The invitation's message key is frontend-only, which is the other half of why the split falls here:
 * server-rendering owner-only copy would owe 24 backend translations no visitor is ever served.)
 *
 * <h3>Not a section, and not collapsible</h3>
 *
 * `ProfileCollapsibleSection` exists for the rail blocks F1 and F2 add — whole sections stacked below a
 * mobile identity card that is already 726px of a 915px viewport. This is one line INSIDE that card,
 * sitting beside the description, which does not collapse either. A heading, a border and a Show/Hide
 * toggle would occupy more vertical space than the sentence they hide, and would turn the identity card
 * into two cards. The heading is a plain `h2` styled as a caption. Decision recorded in
 * `designs/01-now/2026-08-11-profile-owner-statement-f3-implementation.md` §5.
 *
 * <h3>Never a nested island</h3>
 *
 * This renders inside the existing `UserPage` island and introduces no `react-component` container.
 * `createRoot()` discards its element's children, so a nested island is deleted the instant the parent
 * chunk resolves — `user.jte` is the exact template of the 2026-07-28 defect, and it presents as
 * flakiness rather than absence.
 */
/** The profile settings page: the statement field and the sensitive flag live there (the page `MyDashboardPage`'s Edit profile opens). */
export const PROFILE_SETTINGS_PATH = "/settings/update_profile_settings";

export function ProfileStatement(props: {
    localizer: Localizer;
    /** The stored statement, or null/empty when the member has not written one. */
    statement?: string | null;
    /** Whether the person reading this is the person it is about. Drives the title's grammatical person. */
    viewerIsOwner: boolean;
}) {
    const {localizer, statement, viewerIsOwner} = props;
    const trimmedStatement = (statement ?? "").trim();

    // A visitor looking at a profile with no statement gets nothing at all — not a heading, not a
    // placeholder. An empty block on someone else's profile reads as an accusation, and §4 of the design
    // language already bans displaying a negative.
    if (!trimmedStatement && !viewerIsOwner) {
        return null;
    }

    // Two keys, not one with a placeholder. Several target languages cannot render both persons from a
    // single string, which is why the seam minted them as a pair; collapsing them is the one change that
    // would make this block untranslatable.
    const title = viewerIsOwner
        ? localizer("profile.statement.title")
        : localizer("profile.statement.title.visitor");

    return (
        <div className="profile-statement mt-3" data-playwright-id="profile-statement">
            <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {title}
            </h2>
            {trimmedStatement ? (
                <p className="mt-1 text-sm leading-6 text-text-main" data-playwright-id="profile-statement-text">
                    {trimmedStatement}
                </p>
            ) : (
                /* Owner-only, and an invitation rather than an empty state: the member is the one person
                   who can act on it, and the action is one line in Settings. Marked with its own hook so
                   a spec can tell "nothing written yet" apart from "nothing rendered". */
                <p className="mt-1 text-sm leading-6 text-muted-foreground" data-playwright-id="profile-statement-empty">
                    {/* `PROFILE-30` (S7 A76): the invitation IS the way there. It used to ask for a line
                        and give nothing to tap; the settings page that holds the field is one link away. */}
                    <a
                        href={PROFILE_SETTINGS_PATH}
                        className="underline decoration-border underline-offset-4 outline-none hover:text-foreground hover:decoration-foreground focus-visible:rounded-sm focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        data-playwright-id="profile-statement-empty-link"
                    >
                        {localizer("profile.statement.empty.owner")}
                    </a>
                </p>
            )}
        </div>
    );
}

export default ProfileStatement;
